import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { get } from 'lodash'
import AdminCommonFormLayout from '../../layout/AdminCommonFormLayout'
import { showSuccess, showError } from 'actions/Error'
import { adminBeneficiaryStates, adminContactRoles } from 'admin/actions/admin_dropdown_options'
import AdminCommonCompany from '../../companies/AdminCommonCompany'
import { handleSubmitResult } from 'actions/form'
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core'
import {
  adminBeneficiaryVettingList,
  hasPrimaryContactForVetting,
  hasAtLeastOneAddressForVetting
} from '../actions/admin_beneficiary_vetting'
import { adminBeneficiaryVettingDemographicList } from '../actions/admin_beneficiary_vetting_demographic'
import { adminBeneficiaryList } from 'admin/components/beneficiaries/actions/admin_beneficiary'
import { adminContactListVetting } from 'admin/components/contacts/actions/admin_contact'
import { adminAddressListVetting } from 'admin/components/addresses/actions/admin_address'
import {
  vettingToFormInitialValues,
  getDefaultInitialValues,
  formValuesToVettingPayload,
  vettingFormHasValidationErrors
} from './beneficiaryVettingFormUtils'
import { validationVettingSchema } from './AdminBeneficiaryVettingDetailsFormExtended'
import {
  isRevettingFlowFromLocation,
  isFromRevettingNavigation,
  PATH_BENEFICIARIES,
  getVettingPath
} from '../actions/admin_revetting'
import { getRevettingInitialValues, beneficiaryToRevettingFormValues } from './revettingConfig'
import { userList } from '../../../../actions/user'
import { createBeneficiaryVettingNotification } from 'admin/components/notifications/actions/admin_notification'
import { adminActiveNotificationModelList } from 'admin/components/notifications/actions/admin_active_notification_model'

function useIsRevettingFlow() {
  const location = useLocation()
  return isRevettingFlowFromLocation(location)
}

function useFromRevettingNavigation() {
  const location = useLocation()
  return isFromRevettingNavigation(location)
}

export default function AdminBeneficiaryVetting() {
  const user = useSelector(() => userList.getUser())
  const is_vetting_approver = get(user, "is_vetting_approver")
  const history = useHistory()
  const dispatch = useDispatch()
  const { beneficiary_id } = useParams()
  const is_revetting_flow = useIsRevettingFlow()
  const from_revetting_navigation = useFromRevettingNavigation()
  const [is_approving, set_is_approving] = useState(false)
  const [approvalDialogOpen, set_approvalDialogOpen] = useState(false)

  const vetting = useSelector(() => adminBeneficiaryVettingList.getObject(beneficiary_id))

  const beneficiary = useSelector(() =>
    beneficiary_id && is_revetting_flow ? adminBeneficiaryList.getObject(beneficiary_id) : null
  )
  const vettingDemographics = useSelector(() => adminBeneficiaryVettingDemographicList.getVisibleObjects())
  const vetting_loading = useSelector(() => adminBeneficiaryVettingList.isLoading())
  const beneficiary_loading = useSelector(() => adminBeneficiaryList.isLoading())
  const is_loading =
    vetting_loading || (Boolean(is_revetting_flow && beneficiary_id) && beneficiary_loading)
  const is_busy = useSelector(() => adminBeneficiaryVettingList.getIsSavingObject())
  const current_status = get(vetting, 'status')
  const has_primary_contact = useSelector(() => hasPrimaryContactForVetting(beneficiary_id))
  const has_at_least_one_address = useSelector(() => hasAtLeastOneAddressForVetting(beneficiary_id))
  const active_notification_models = useSelector(() => adminActiveNotificationModelList.getVisibleObjects())

  const title =
    beneficiary_id && (vetting || beneficiary)
      ? (get(vetting, 'company_legal_name') ||
        get(beneficiary, 'company.name') ||
        'Beneficiary Vetting')
      : 'New Beneficiary Vetting'

  const initial_values = (() => {
    if (!beneficiary_id) return getDefaultInitialValues()
    if (is_revetting_flow) {
      const fromBeneficiary = beneficiaryToRevettingFormValues(beneficiary)
      return getRevettingInitialValues(getDefaultInitialValues(), fromBeneficiary)
    }
    return vettingToFormInitialValues(vetting, vettingDemographics)
  })()

  const vettingRecordId = get(vetting, 'id')
  const vettingCompany = get(vetting, 'company')
  let companyForAddressContactList = null

  if (vettingCompany) {
    companyForAddressContactList = vettingCompany
  } else if (is_revetting_flow) {
    companyForAddressContactList = get(beneficiary, 'company.id')
  }

  const addressContactListFilter = (() => {
    if (is_revetting_flow && companyForAddressContactList !== null) { 
      return { company: companyForAddressContactList } 
    }
    else if (!is_revetting_flow && beneficiary_id) { 
      return { beneficiary_vetting: beneficiary_id } 
    }

    return undefined;
  })()

  useEffect(() => {
    if (beneficiary_id) {
      dispatch(adminBeneficiaryVettingList.invalidateObject(beneficiary_id))
      dispatch(adminBeneficiaryVettingList.ensureObjectLoaded(beneficiary_id))
      if (is_revetting_flow) {
        dispatch(adminBeneficiaryList.ensureObjectLoaded(beneficiary_id))
      }
      dispatch(adminBeneficiaryVettingDemographicList.updateListFilter({ beneficiary_vetting: beneficiary_id }))
      dispatch(adminBeneficiaryVettingDemographicList.fetchListIfNeeded())
      if (addressContactListFilter) {
        dispatch(adminContactListVetting.invalidateList())
        dispatch(adminContactListVetting.updateListFilter(addressContactListFilter))
        dispatch(adminContactListVetting.fetchListIfNeeded())
        dispatch(adminAddressListVetting.invalidateList())
        dispatch(adminAddressListVetting.updateListFilter(addressContactListFilter))
        dispatch(adminAddressListVetting.fetchListIfNeeded())
      }
    }
    dispatch(adminBeneficiaryStates.fetchListIfNeeded())
    dispatch(adminContactRoles.fetchListIfNeeded())
    dispatch(adminActiveNotificationModelList.fetchListIfNeeded())
  }, [dispatch, beneficiary_id, is_revetting_flow, vettingRecordId, companyForAddressContactList])

  const vettingNotificationContext = {
    fallbackVettingRecord: vetting,
    user,
    activeNotificationModels: active_notification_models
  }

  const onSave = (values, formik_funcs) => {
    const payload = formValuesToVettingPayload(values)
    const previous_status = get(vetting, 'status')
    const new_status = get(values, 'status')
    const on_ok = function (json) {
      dispatch(adminBeneficiaryVettingList.invalidateList())
      if (beneficiary_id) {
        dispatch(adminBeneficiaryVettingList.invalidateObject(beneficiary_id))
        dispatch(adminBeneficiaryVettingList.ensureObjectLoaded(beneficiary_id))
      }
      showSuccess('Saved', 'Beneficiary vetting saved')
      
      if (!beneficiary_id) {
        history.push(`/admin/beneficiary_vetting/${json.id}`)
      }
    }

    if (beneficiary_id) {
      if (!is_revetting_flow) {
        if (!payload.id) {
          const vettingIdFromStore = get(vetting, 'id')
          payload.id = vettingIdFromStore || beneficiary_id
        }
        return dispatch(adminBeneficiaryVettingList.saveObject(payload))
          .then(async (res) => {
            if (previous_status !== 'declined' && new_status === 'declined') {
              await createBeneficiaryVettingNotification(dispatch, res, 'declined', vettingNotificationContext)
            }
            handleSubmitResult({ res, formik_funcs, on_ok })
          })
      }
    }
    return dispatch(adminBeneficiaryVettingList.saveNewObject(payload))
      .then(async (res) => {
        await createBeneficiaryVettingNotification(dispatch, res, 'created', vettingNotificationContext)
        handleSubmitResult({ res, formik_funcs, on_ok })
        history.push(`/admin/beneficiary_vetting/${res.id}`)
      })
  }

  const openApprovalDialog = () => {
    if (beneficiary_id && !has_primary_contact) {
      showError('Cannot approve', 'Add at least one primary contact before approving.')
      return
    }
    if (beneficiary_id && !has_at_least_one_address) {
      showError('Cannot approve', 'Add at least one address before approving.')
      return
    }
    set_approvalDialogOpen(true)
  }

  const closeApprovalDialog = () => set_approvalDialogOpen(false)

  const confirmApproval = async () => {
    closeApprovalDialog()
    set_is_approving(true)
    try {
      await dispatch(adminBeneficiaryVettingList.approveVetting({ beneficiary_vetting_id: beneficiary_id }))
      await createBeneficiaryVettingNotification(dispatch, vetting, 'approved', vettingNotificationContext)
      showSuccess('Beneficiary Approved', 'This beneficiary has been approved successfully.')
      dispatch(adminBeneficiaryVettingList.invalidateObject(beneficiary_id))
      dispatch(adminBeneficiaryVettingList.ensureObjectLoaded(beneficiary_id))
      history.push(PATH_BENEFICIARIES)
    } catch (err) {
      showError('Approval Failed', err?.message || 'An error occurred while approving the beneficiary.')
    } finally {
      set_is_approving(false)
    }
  }

  const breadcrumbs = is_revetting_flow
    ? [
      { name: 'admin_home' },
      { name: 'beneficiaries', label: 'Beneficiaries', url: PATH_BENEFICIARIES },
      {
        name: 'beneficiary_vetting_beneficiary',
        label: title,
        url: getVettingPath(beneficiary_id)
      }
    ]
    : [
      { name: 'admin_home' },
      { name: 'beneficiary_vetting', label: 'Beneficiary Vetting', url: '/admin/beneficiaries_vetting' },
      {
        name: 'beneficiary_vetting_beneficiary',
        label: title,
        url: `/admin/beneficiary_vetting/${beneficiary_id || ''}`
      }
    ]

  return (
    <AdminCommonFormLayout
      breadcrumbs={breadcrumbs}
      is_busy={is_busy}
      is_loading={is_loading}
      title={title}
      initial_values={initial_values}
      validationSchema={validationVettingSchema}
      validateOnMount={true}
      onSave={onSave}
    >
      {({ formik_props }) => (
        <>
          <AdminCommonCompany
            company_id={get(vetting, 'company') || get(vetting, 'company_id') || get(beneficiary, 'company.id') || formik_props?.values?.company?.id}
            beneficiary_vetting_ref={formik_props?.values?.id}
            formik_props={formik_props}
            form_variation="beneficiary_vetting"
            is_loading={is_loading}
          />
          <div style={{ position: 'fixed', bottom: 15, right: 200, zIndex: 100 }}>
            {(current_status === 'declined' ||
              (from_revetting_navigation && current_status === 'approved')) ?
              null
              : (<Button
                variant="outlined"
                size="large"
                color="primary"
                disabled={
                  !beneficiary_id ||
                  current_status === 'approved' ||
                  current_status === 'declined' ||
                  is_revetting_flow ||
                  (vettingFormHasValidationErrors(formik_props.errors) && formik_props.submitCount > 0) ||
                  formik_props.isSubmitting ||
                  is_approving ||
                  is_busy  ||
                  !is_vetting_approver
                }
                onClick={openApprovalDialog}
              >
                Approve Beneficiary
              </Button>)
            }
          </div>
          <Dialog
            open={approvalDialogOpen}
            onClose={closeApprovalDialog}
            aria-labelledby="approve-beneficiary-dialog-title"
            aria-describedby="approve-beneficiary-dialog-description"
          >
            <DialogTitle id="approve-beneficiary-dialog-title">Approve Beneficiary</DialogTitle>
            <DialogContent>
              <DialogContentText id="approve-beneficiary-dialog-description">
                Are you sure you want to approve this beneficiary?
                <br /><br />
                Please note this will create/update a beneficiary.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeApprovalDialog}>Cancel</Button>
              <Button onClick={confirmApproval} color="primary" variant="contained" autoFocus>
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </AdminCommonFormLayout>
  )
}